// What a visit needs (BL-027): each licence its job type needs, held by someone on the visit and
// valid on the visit's own day (through its expiry date). Whoever leads doesn't matter: a licensed
// fitter can be on a visit an apprentice leads. The server (scheduling/licence-check.ts) and the
// iPad (sync-client/src/licences.ts) both decide by this, so they never disagree.

import { dateText } from '@flowology/kernel';

/** A licence someone holds: its kind and expiry only (null: it doesn't expire). */
export interface HeldLicence {
  staffId: string;
  kindId: string;
  expiresOn: string | null;
}

/** A kind nobody on the visit holds valid that day, and the latest-lasting one that falls short. */
export interface LicenceShortfall {
  kindId: string;
  holder: HeldLicence | null;
}

/**
 * The kinds of `required` nobody in `crew` holds valid on `day` (a company-local date). A visit with
 * nobody on it has none: there's nobody to check until someone is.
 */
export function licenceShortfalls(
  required: readonly string[],
  crew: readonly string[],
  held: readonly HeldLicence[],
  day: string,
): LicenceShortfall[] {
  if (!crew.length) return [];
  const on = new Set(crew);
  return [...new Set(required)].flatMap((kindId) => {
    const theirs = held.filter((l) => l.kindId === kindId && on.has(l.staffId));
    if (theirs.some((l) => !l.expiresOn || l.expiresOn >= day)) return [];
    const holder = [...theirs].sort((a, b) => String(b.expiresOn).localeCompare(String(a.expiresOn)))[0];
    return [{ kindId, holder: holder ?? null }];
  });
}

export type ShortfallWhy = 'none' | 'expired' | 'expires_before_visit';

/**
 * A shortfall in words, as the office and the iPad say it: "Gas fitter (class B): nobody on this
 * visit holds it.", "…: Kim’s expired Sep 30, 2026.", "…: Kim’s expires Oct 25, 2026, before the
 * visit on Oct 28, 2026." Expired is as of `today`; one still valid today lapses before the visit.
 */
export function shortfallWords(
  s: { kindName: string; holderName: string | null; expiresOn: string | null },
  day: string,
  today: string,
  timeZone: string,
): { why: ShortfallWhy; text: string } {
  if (!s.holderName || !s.expiresOn)
    return { why: 'none', text: `${s.kindName}: nobody on this visit holds it.` };
  const on = dateText(s.expiresOn, timeZone);
  return s.expiresOn < today
    ? { why: 'expired', text: `${s.kindName}: ${s.holderName}’s expired ${on}.` }
    : {
        why: 'expires_before_visit',
        text: `${s.kindName}: ${s.holderName}’s expires ${on}, before the visit on ${dateText(day, timeZone)}.`,
      };
}
